















import Vue, { VueConstructor } from "vue";
import * as PIXI from "pixi.js";
import { Application } from "pixi.js";
import { AssetLoader, AssetPack } from "@/game/libs/engine/loader/asset-loader";
import { Record } from "@/common/api/leaderboard/Record";
import SkiGame from "@/game/skifree/ski-game";
import Modal from "@/components/Modal.vue";
import addLoaderAssets from "./skifree/assets";
import { trackEvent } from "@/js/tracking/gtag";
import {
  create as createBridge,
  state as createState,
  UIBridge,
  GameState
} from "@/game/libs/ui-bridge";
import { Mutations } from "@/store/leaderboard";

interface Refs {
  $refs: {
    /** The game element container */
    canvas: HTMLCanvasElement;
    container: HTMLDivElement;
  };
}

interface Opts {
  $options: {
    bridge: UIBridge;
    game?: SkiGame;
    onResize: () => void;
  };
}

export default (Vue as VueConstructor<Vue & Refs & Opts>).extend({
  components: {
    Modal,
    Leaderboard: () => import("./Leaderboard.vue"),
    GameOver: () => import("./GameOver.vue"),
    Instructions: () => import("./Instructions.vue")
  },
  data() {
    return {
      state: createState(),
      hasStarted: false,
      showLeaderboard: false,
      showInstructions: true
    };
  },
  watch: {
    modalOpen(isOpen: boolean): void {
      this.pause(isOpen);
    }
  },
  computed: {
    modalOpen(): boolean {
      return (
        this.showLeaderboard || this.showInstructions || this.state.gameOver
      );
    },
    modalContent(): any {
      if (this.showLeaderboard) {
        return "Leaderboard";
      } else if (this.state.gameOver) {
        return "GameOver";
      } else if (this.showInstructions) {
        return "Instructions";
      }
      return "div"; // We should never get here.
    },
    hasClose(): boolean {
      if (this.showLeaderboard) {
        return true;
      } else if (this.state.gameOver) {
        return false;
      } else if (this.showInstructions) {
        return true;
      }
      return true; // We should never get here.
    }
  },
  created(): void {
    this.$options.onResize = this.onResize.bind(this);
    // Wire methods to bridge
    this.$options.bridge = createBridge(
      this.state,
      () => this.instructionsClicked(),
      () => this.leaderboardClicked(),
      () => this.gameEnded()
    );
  },
  mounted(): void {
    trackEvent("page_load");
    PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.NEAREST;
    var loader = ((this as any).loader = new AssetLoader());
    addLoaderAssets(loader);
    loader
      .load()
      .then(
        assetPack =>
          (this.$options.game = new SkiGame(
            this.$refs.canvas,
            this.$refs.container.offsetWidth,
            this.$refs.container.offsetHeight,
            assetPack,
            this.$options.bridge,
            this.onResize
          ))
      );

    window.addEventListener("resize", this.$options.onResize);
    window.setTimeout(callResize, 1000);
  },
  methods: {
    onCloseClick() {
      if (this.showInstructions && !this.hasStarted) {
        trackEvent("start_game");
        this.hasStarted = true;
      }
      this.showInstructions = false;
      this.showLeaderboard = false;
    },
    onResize(): void {
      if (this.$options.game) {
        const w: number = this.$refs.container.offsetWidth;
        const h: number = this.$refs.container.offsetHeight;
        this.$options.game.resize(w, h);
      }
    },
    gameEnded(): void {
      // Record the metrics to Vuex store.
      const userRecord: Record = {
        initials: "",
        score: this.state.score,
        medal: this.state.medal
      };
      this.$store.commit(
        `leaderboard/${Mutations.SET_CURRENT_RECORD}`,
        userRecord
      );
      this.state.gameOver = true;
      trackEvent("game_end");
    },
    restartGame(): void {
      this.state.gameOver = false;
      this.showLeaderboard = false;
      this.showInstructions = false;
      if (this.$options.game) {
        this.$options.game.restart();
      }
      trackEvent("game_play_again");
    },
    instructionsClicked(): void {
      trackEvent("click_instructions");
      this.showInstructions = true;
    },
    leaderboardClicked(): void {
      trackEvent("click_leaderboard");
      this.showLeaderboard = true;
    },
    /**
     * Pause or play the gameplay.
     */
    pause(shouldPause: boolean): void {
      if (this.$options.game) {
        this.$options.game.pause(shouldPause);
        if (!shouldPause) {
          this.$refs.canvas.focus();
        }
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.$options.onResize);
    if (this.$options.game) {
      this.$options.game.dispose();
    }
    window.location.reload(true);
  }
});

function callResize() {
  if (typeof Event === "function") {
    // modern browsers
    window.dispatchEvent(new Event("resize"));
  } else {
    // for IE and other old browsers
    // causes deprecation warning on modern browsers
    var evt = window.document.createEvent("UIEvents");
    (evt as any).initUIEvent("resize", true, false, window, 0);
    window.dispatchEvent(evt);
  }
}
